import React, { useEffect, useState } from "react";
import {
  getGpaPercentAverages,
  getLessonFutureLevelAverage,
  getOverviewTexts,
  saveOverviewTexts,
} from "../../../lib/studentgparigorsummary";
import style from "./globalstudentgparigorsummary.module.css";
import { Toast, ToastContainer } from "react-bootstrap";
import { getColorClass, getHeaderClass } from "./globalstudentgparigorsummary";

const levelClassMap = {
  Ovr: style.apCell,
  AP: style.apCell,
  HR: style.hrCell,
  CC: style.ccCell,
  EA: style.eaCell,
  RG: style.rgCell,
};

export default function GlobalStudentAverageGpaSummary({
  studentId,
  isStudent = false,
}) {
  const [current, setCurrent] = useState({});
  const [future, setFuture] = useState({});
  const [overview, setOverview] = useState({});
  const [toastMessage, setToastMessage] = useState("");
  const [showToast, setShowToast] = useState(false);
  const [collapseOpenClose, setCollapseOpenClose] = useState(true);
  const [levels, setLevels] = useState([]);

  const collapseAll = () => {
    setCollapseOpenClose(!collapseOpenClose);
  };

  const showToastMessage = (message) => {
    setToastMessage(message);
    setShowToast(true);
    setTimeout(() => setShowToast(false), 3000);
  };

  const loadData = async () => {
    const currentRes = await getGpaPercentAverages(studentId);
    const futureRes = await getLessonFutureLevelAverage(studentId);
    const overviewRes = await getOverviewTexts(studentId);

    const currentMap = {};
    const futureMap = {};
    const levelSet = new Set();

    currentRes.forEach((item) => {
      const lvl = item.level === "Overall" ? "Ovr" : item.level;
      currentMap[lvl] = item.average ?? 0;
      levelSet.add(lvl);
    });

    futureRes.forEach((item) => {
      const lvl = item.level === "Overall" ? "Ovr" : item.level;
      futureMap[lvl] = item.average ?? 0;
      levelSet.add(lvl);
    });

    const overviewMap = {};
    overviewRes.forEach((item) => {
      overviewMap[item.type] = item.desc;
    });

    setCurrent(currentMap);
    setFuture(futureMap);
    setOverview(overviewMap);
  // Custom sort order for levels
  const customOrder = ["AP", "CC", "HR", "EA", "RG", "OT"];
  const sortedLevels = customOrder.filter(lvl => levelSet.has(lvl));
  setLevels(["Ovr", ...sortedLevels]);
  };

  useEffect(() => {
    loadData();
  }, [studentId]);

  const handleOverviewChange = (value) => {
    setOverview((prev) => ({ ...prev, caverage: value }));
  };

  const handleUpdate = async () => {
    await saveOverviewTexts(studentId, { caverage: overview.caverage || "" });
    showToastMessage("Update successful!");
    loadData();
  };

  const getValue = (map, level) => {
    if (level === "Ovr") {
      const values = levels
        .filter((lvl) => lvl !== "Ovr")
        .map((lvl) => parseFloat(map[lvl] || 0))
        .filter((val) => val > 0); // 🔥 0 olanları filtrele

      if (values.length === 0) return "0.0";

      const sum = values.reduce((acc, val) => acc + val, 0);
      return (sum / values.length).toFixed(2);
    }

    return map[level] !== undefined ? parseFloat(map[level]).toFixed(2) : "0.0";
  };

  return (
    <div className="card p-3">
      <div className="personal-info-head">
        <h4>GPA</h4>
        <ToastContainer
          style={{ top: "1rem", right: "1rem", position: "fixed" }}
          className="p-3"
        >
          <Toast
            show={showToast}
            onClose={() => setShowToast(false)}
            autohide
            delay={3000}
          >
            <Toast.Header>
              <strong className="me-auto">Notification</strong>
              <button
                className="btn-close"
                onClick={() => setShowToast(false)}
              />
            </Toast.Header>
            <Toast.Body>{toastMessage}</Toast.Body>
          </Toast>
        </ToastContainer>
      </div>

      <div className={`collapse ${collapseOpenClose ? "show" : ""}`}>
        <table className="table table-bordered text-center">
          <thead>
            <tr>
              <th style={{ width: "150px" }}></th>
              {levels.map((lvl) => (
                <th key={lvl} className={getHeaderClass(lvl)}>{lvl}</th>
              ))}
              <th style={{ width: "400px" }}>Overview</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>Current</td>
              {levels.map((lvl) => {
                const val = parseFloat(getValue(current, lvl));
                return (
                  <td key={lvl}  className={val.toFixed(2) > 0 ? getHeaderClass(lvl): ""}>
                    {val.toFixed(2)}
                  </td>
                );
              })}
              <td rowSpan={3}>
                {!isStudent ? (
                  <textarea
                    rows={6}
                    className="form-control"
                    value={overview["caverage"] || ""}
                    onChange={(e) => handleOverviewChange(e.target.value)}
                  />
                ) : (
                  <div className="bg-light border rounded p-2">
                    {overview["caverage"]}
                  </div>
                )}
              </td>
            </tr>
            <tr>
              <td>Future</td>
              {levels.map((lvl) => {
                const val = parseFloat(getValue(future, lvl));
                return (
                  <td key={lvl} className={val.toFixed(2) > 0 ? getHeaderClass(lvl): ""}>
                    {val.toFixed(2)}
                  </td>
                );
              })}
            </tr>
            <tr>
              <td>Total</td>
              {levels.map((lvl) => {
                if (lvl === "Ovr") {
                  // Collect all current and future values for all levels except Ovr
                  const allValues = levels
                    .filter((l) => l !== "Ovr")
                    .flatMap((l) => [parseFloat(current[l] || 0), parseFloat(future[l] || 0)])
                    .filter((val) => val > 0);
                  const overallAvg =
                    allValues.length > 0
                      ? (allValues.reduce((a, b) => a + b, 0) / allValues.length).toFixed(2)
                      : "0.0";
                  return (
                    <td key={lvl} className={overallAvg > 0 ? getHeaderClass(lvl): ""} >
                      {overallAvg}
                    </td>
                  );
                } else {
                  const curr = parseFloat(current[lvl] || 0);
                  const fut = parseFloat(future[lvl] || 0);
                  const values = [];
                  if (curr > 0) values.push(curr);
                  if (fut > 0) values.push(fut);
                  const avg =
                    values.length > 0
                      ? (
                          values.reduce((a, b) => a + b, 0) / values.length
                        ).toFixed(2)
                      : "0.0";
                  return (
                    <td key={lvl}className={(avg > 0 ? getHeaderClass(lvl): "" ) + " fw-bold"}>
                      {avg}
                    </td>
                  );
                }
              })}
            </tr>
          </tbody>
        </table>

        {!isStudent && (
          <div className="text-end">
            <button className="btn btn-primary" onClick={handleUpdate}>
              Update
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
